import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cx } from '../../lib/format';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'tinted' | 'plain';
type Size = 'sm' | 'md' | 'lg' | 'icon';

const variants: Record<Variant, string> = {
  primary: 'bg-primary text-on-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_1px_2px_rgba(0,0,0,0.12)] hover:bg-primary-hover active:bg-primary-press',
  secondary: 'bg-surface text-ink shadow-sm hover:bg-surface-2 active:bg-active',
  tinted: 'bg-primary-soft text-primary-text hover:bg-primary-soft-2',
  ghost: 'text-ink-2 hover:bg-hover hover:text-ink active:bg-active',
  danger: 'bg-danger-soft text-danger hover:brightness-[0.97] active:brightness-95',
  plain: 'text-primary-text underline-offset-2 hover:underline !px-0 !h-auto',
};

// Altura mínima de 44 px em telas de toque para os tamanhos principais.
const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-[10px] coarse:h-10 [&_svg]:size-4',
  md: 'h-10 px-4 text-[14px] gap-2 rounded-[12px] coarse:h-11 [&_svg]:size-[18px]',
  lg: 'h-12 px-5 text-[15px] gap-2 rounded-[14px] [&_svg]:size-5',
  icon: 'h-10 w-10 justify-center rounded-[12px] coarse:h-11 coarse:w-11 [&_svg]:size-[18px]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

/** Botão do sistema: resposta imediata ao toque (escala sutil), foco visível e estado de envio. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'relative inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap font-[560] tracking-[-0.01em]',
        'transition-[background-color,color,box-shadow,filter,transform] duration-[160ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] active:scale-[0.97]',
        'disabled:pointer-events-none disabled:opacity-45',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});
